/**
 * Sessions: .aperto files (zip: session.json + exact frame data) and a local
 * IndexedDB autosave so work survives reloads. Nothing leaves the machine.
 */
import { batch, effect } from '@preact/signals';
import { unzipSync, zipSync, strFromU8, strToU8 } from 'fflate';
import type { RawFrame } from '../core/types';
import { sanitizeRois } from '../core/roi';
import { mergeChecked, validQuantity } from './validate';
import { download } from './exporters';
import {
  busy,
  compareMode,
  display,
  frames,
  metricSettings,
  notify,
  pinned,
  pipeline,
  profileView,
  quantity,
  rawOptions,
  rois,
  sampling,
  selectedId,
  spatial,
  spectrumSettings,
  type PinnedProfile,
  type SlotId,
} from './store';

const SLOTS: SlotId[] = ['A', 'B', 'dark', 'flat'];
const FORMAT = 'aperto-session';
const VERSION = 1;

/** JSON with non-finite numbers and typed arrays preserved. */
export function toJson(v: unknown): string {
  return JSON.stringify(v, (_k, x) => {
    if (typeof x === 'number' && !Number.isFinite(x)) return { $num: String(x) };
    if (x instanceof Float64Array || x instanceof Float32Array) return { $f64: Array.from(x, (n) => (Number.isFinite(n) ? n : String(n))) };
    return x;
  });
}

export function fromJson<T>(s: string): T {
  return JSON.parse(s, (_k, x) => {
    if (x && typeof x === 'object' && '$num' in x) return Number(x.$num);
    if (x && typeof x === 'object' && '$f64' in x) return Float64Array.from(x.$f64 as (number | string)[], (n) => Number(n));
    return x;
  }) as T;
}

interface FrameHeader {
  width: number;
  height: number;
  channels: 1 | 3;
  dtype: 'u8' | 'u16' | 'f32';
  scale: number;
  maxCode: number;
  isFloat: boolean;
  clipLevel: number;
  meta: RawFrame['meta'];
}

export interface SessionState {
  format: string;
  version: number;
  savedAt: string;
  settings: Record<string, unknown>;
  rois: unknown;
  selectedId: string | null;
  pinned: PinnedProfile[];
  frames: Partial<Record<SlotId, FrameHeader>>;
}

function header(f: RawFrame): FrameHeader {
  const dtype = f.data instanceof Uint8Array ? 'u8' : f.data instanceof Uint16Array ? 'u16' : 'f32';
  return {
    width: f.width,
    height: f.height,
    channels: f.channels,
    dtype,
    scale: f.scale,
    maxCode: f.maxCode,
    isFloat: f.isFloat,
    clipLevel: f.clipLevel,
    meta: f.meta,
  };
}

function bytesOf(f: RawFrame): Uint8Array {
  return new Uint8Array(f.data.buffer, f.data.byteOffset, f.data.byteLength);
}

export function frameFrom(h: FrameHeader, bytes: Uint8Array): RawFrame {
  const buf = bytes.slice().buffer;
  const data = h.dtype === 'u8' ? new Uint8Array(buf) : h.dtype === 'u16' ? new Uint16Array(buf) : new Float32Array(buf);
  return {
    width: h.width,
    height: h.height,
    channels: h.channels,
    data,
    scale: h.scale,
    maxCode: h.maxCode,
    isFloat: h.isFloat,
    clipLevel: h.clipLevel,
    meta: { ...h.meta, origin: 'session' },
  };
}

export function captureState(includeFrames: boolean): SessionState {
  const fr: SessionState['frames'] = {};
  // Live frames change continuously and are never saved.
  if (includeFrames)
    for (const s of SLOTS) if (frames[s].value && frames[s].value!.meta.origin !== 'live') fr[s] = header(frames[s].value!);
  return {
    format: FORMAT,
    version: VERSION,
    savedAt: new Date().toISOString(),
    settings: {
      pipeline: pipeline.value,
      quantity: quantity.value,
      sampling: sampling.value,
      metricSettings: metricSettings.value,
      spectrumSettings: spectrumSettings.value,
      spatial: spatial.value,
      display: display.value,
      compareMode: compareMode.value,
      profileView: profileView.value,
      rawOptions: rawOptions.value,
    },
    rois: rois.value,
    selectedId: selectedId.value,
    pinned: pinned.value,
    frames: fr,
  };
}

export function applyState(st: SessionState, loaded: Partial<Record<SlotId, RawFrame>>) {
  if (st.format !== FORMAT) throw new Error('Not an Aperto session file');
  const s = st.settings as Record<string, never>;
  batch(() => {
    pipeline.value = mergeChecked(pipeline.value, s.pipeline);
    // Calibration points and width levels are arrays: keep only well-formed entries.
    pipeline.value = {
      ...pipeline.value,
      calibration: {
        ...pipeline.value.calibration,
        points: (pipeline.value.calibration.points as unknown[]).filter(
          (p): p is { measured: number; reference: number } =>
            !!p &&
            typeof p === 'object' &&
            typeof (p as { measured: unknown }).measured === 'number' &&
            typeof (p as { reference: unknown }).reference === 'number',
        ),
      },
    };
    if (validQuantity(s.quantity)) quantity.value = s.quantity;
    sampling.value = mergeChecked(sampling.value, s.sampling);
    if (!(sampling.value.step > 0)) sampling.value = { ...sampling.value, step: 1 };
    metricSettings.value = mergeChecked(metricSettings.value, s.metricSettings);
    metricSettings.value = {
      ...metricSettings.value,
      widthLevels: metricSettings.value.widthLevels.filter((l) => typeof l === 'number' && l > 0 && l < 1),
    };
    spectrumSettings.value = mergeChecked(spectrumSettings.value, s.spectrumSettings);
    spatial.value = mergeChecked(spatial.value, s.spatial);
    if (!(spatial.value.perPixel > 0)) spatial.value = { ...spatial.value, perPixel: 1 };
    display.value = mergeChecked(display.value, s.display);
    profileView.value = mergeChecked(profileView.value, s.profileView);
    rawOptions.value = mergeChecked(rawOptions.value, s.rawOptions);
    // A session with images replaces every slot; a settings-only session keeps the current images.
    const hasImages = Object.keys(st.frames ?? {}).length > 0;
    for (const slot of SLOTS) if (hasImages) frames[slot].value = loaded[slot] ?? null;
    if (['A', 'B', 'diff', 'ratio', 'pct'].includes(s.compareMode)) compareMode.value = s.compareMode;
    const { rois: valid, dropped } = sanitizeRois(st.rois);
    rois.value = valid;
    if (dropped) notify('warn', `${dropped} malformed ROI(s) in the session were skipped`);
    selectedId.value = valid.some((r) => r.id === st.selectedId) ? st.selectedId : null;
    pinned.value = (Array.isArray(st.pinned) ? st.pinned : []).filter(
      (p) => p && p.x instanceof Float64Array && p.y instanceof Float64Array && p.x.length === p.y.length && typeof p.color === 'string',
    );
  });
}

export function buildSessionZip(embed: boolean, level: 0 | 1 | 6 = 6): Uint8Array {
  const st = captureState(embed);
  const files: Record<string, Uint8Array> = { 'session.json': strToU8(toJson(st)) };
  if (embed) for (const s of SLOTS) if (st.frames[s]) files[`frames/${s}.bin`] = bytesOf(frames[s].value!);
  return zipSync(files, { level });
}

export function readSessionZip(bytes: Uint8Array): { state: SessionState; frames: Partial<Record<SlotId, RawFrame>> } {
  const files = unzipSync(bytes);
  if (!files['session.json']) throw new Error('session.json missing');
  const state = fromJson<SessionState>(strFromU8(files['session.json']));
  const out: Partial<Record<SlotId, RawFrame>> = {};
  for (const [slot, h] of Object.entries(state.frames ?? {}) as [SlotId, FrameHeader][]) {
    const b = files[`frames/${slot}.bin`];
    if (b) out[slot] = frameFrom(h, b);
  }
  return { state, frames: out };
}

export function saveSession(embed: boolean) {
  busy.value = 'Saving session…';
  setTimeout(() => {
    try {
      const zip = buildSessionZip(embed, 1);
      const name = (frames.A.value?.meta.name ?? 'session').replace(/\.[^.]+$/, '');
      download(new Blob([zip as BlobPart], { type: 'application/zip' }), `${name}.aperto`);
    } catch (e) {
      notify('error', `Could not save session: ${(e as Error).message}`);
    } finally {
      busy.value = null;
    }
  }, 20);
}

export async function loadSessionBlob(blob: Blob) {
  busy.value = 'Loading session…';
  try {
    const { state, frames: fr } = readSessionZip(new Uint8Array(await blob.arrayBuffer()));
    applyState(state, fr);
    notify('success', `Session restored (saved ${new Date(state.savedAt).toLocaleString()})`);
  } catch (e) {
    notify('error', `Could not load session: ${(e as Error).message}`);
  } finally {
    busy.value = null;
  }
}

export function loadSessionFile() {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.aperto,application/zip';
  input.onchange = () => {
    const f = input.files?.[0];
    if (f) loadSessionBlob(f);
  };
  input.click();
}

// ------------------------------------------------------------------ IndexedDB autosave

const DB = 'aperto';
const STORE = 'autosave';

function openDb(): Promise<IDBDatabase> {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

async function idb<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });
}

const MAX_AUTOSAVE_BYTES = 400e6;
let restoring = false;

/** Start autosaving settings (debounced) and frames (when they change). */
export function startAutosave() {
  let timer = 0;
  let firstRun = true;
  effect(() => {
    const st = captureState(true);
    // Never overwrite the previous session before the user changes something.
    if (firstRun) {
      firstRun = false;
      return;
    }
    if (restoring) return;
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      idb('readwrite', (s) => s.put(toJson(st), 'state')).catch(() => undefined);
    }, 800);
  });
  for (const slot of SLOTS) {
    let first = true;
    effect(() => {
      const f = frames[slot].value;
      if (first) {
        first = false;
        return;
      }
      if (restoring) return;
      if (!f) {
        idb('readwrite', (s) => s.delete(`frame:${slot}`)).catch(() => undefined);
        return;
      }
      if (f.meta.origin === 'live') return;
      if (f.data.byteLength > MAX_AUTOSAVE_BYTES) return;
      idb('readwrite', (s) => s.put({ header: header(f), data: f.data }, `frame:${slot}`)).catch(() => undefined);
    });
  }
}

/** Offer to restore the previous session if one exists. */
export async function offerRestore() {
  try {
    const raw = await idb<string | undefined>('readonly', (s) => s.get('state'));
    if (!raw) return;
    const st = fromJson<SessionState>(raw);
    const names = Object.values(st.frames ?? {}).map((h) => h.meta.name);
    if (!names.length && !(st.rois as unknown[])?.length) return;
    notify('info', `Previous session found${names.length ? ` (${names.join(', ')})` : ''}.`, {
      timeout: 20000,
      action: {
        label: 'Restore',
        run: async () => {
          restoring = true;
          try {
            const loaded: Partial<Record<SlotId, RawFrame>> = {};
            for (const slot of SLOTS) {
              const rec = await idb<{ header: FrameHeader; data: RawFrame['data'] } | undefined>('readonly', (s) => s.get(`frame:${slot}`));
              if (rec && st.frames?.[slot])
                loaded[slot] = frameFrom(rec.header, new Uint8Array(rec.data.buffer, rec.data.byteOffset, rec.data.byteLength));
            }
            applyState(st, loaded);
            notify('success', 'Previous session restored');
          } catch (e) {
            notify('error', `Restore failed: ${(e as Error).message}`);
          } finally {
            restoring = false;
          }
        },
      },
    });
  } catch {
    /* IndexedDB unavailable (private mode etc.) */
  }
}
